/**
 * Mock DD Assistant
 *
 * Provides simulated AI responses for MOCK MODE only.
 * Returns predefined responses based on query patterns.
 * No network calls - works completely offline.
 *
 * Implements IDDAssistant interface for seamless swapping.
 */

import type {
  IDDAssistant,
  DDAssistantContext,
  DDAssistantResponse,
  DDAssistantMode,
  DDConversationSummary,
  DDSource,
} from './assistant.types';
import type { DDChatMessage } from '@/lib/supabase/edge-functions';

export class MockAssistant implements IDDAssistant {
  private conversationMessages: Map<string, DDChatMessage[]> = new Map();
  private messageCounter = 0;

  /**
   * Get the mode identifier
   */
  getMode(): DDAssistantMode {
    return 'mvp'; // Mock mode reports as MVP
  }

  /**
   * Send a message and get a simulated response.
   */
  async chat(context: DDAssistantContext): Promise<DDAssistantResponse> {
    const startTime = Date.now();
    const conversationKey = `${context.investorId}-${context.startupId}`;

    // Simulate thinking delay (300-800ms)
    await this.simulateDelay(300 + Math.random() * 500);

    // Get or create conversation history
    let history = this.conversationMessages.get(conversationKey) || [];

    // Add user message to history
    const userMessage: DDChatMessage = {
      id: `msg_${++this.messageCounter}`,
      role: 'user',
      content: context.message,
      timestamp: Date.now(),
    };
    history.push(userMessage);

    // Generate mock response based on query — clearly labeled as demo data
    const rawContent = this.generateMockResponse(context.message, context.startupContext);
    const responseContent = `> **Demo mode** — The following uses sample data for illustration only.\n\n${rawContent}`;
    const sources = this.getMockSources(context.message);

    // Create assistant message
    const assistantMessage: DDChatMessage = {
      id: `msg_${++this.messageCounter}`,
      role: 'assistant',
      content: responseContent,
      timestamp: Date.now(),
      metadata: { sources: sources.map(s => s.label) },
    };
    history.push(assistantMessage);

    // Update stored history
    this.conversationMessages.set(conversationKey, history);

    const processingTime = Date.now() - startTime;

    return {
      message: assistantMessage,
      sources,
      suggestedQuestions: this.getSuggestedQuestions(context.message),
      topicsDiscussed: this.extractTopics(context.message),
      usage: {
        inputTokens: Math.floor(context.message.length / 4),
        outputTokens: Math.floor(responseContent.length / 4),
        totalTokens: Math.floor((context.message.length + responseContent.length) / 4),
      },
      mode: 'mvp',
      processingTimeMs: processingTime,
    };
  }

  /**
   * Get conversation history for a startup-investor pair.
   */
  async getHistory(investorId: string, startupId: string): Promise<DDConversationSummary[]> {
    const conversationKey = `${investorId}-${startupId}`;
    const messages = this.conversationMessages.get(conversationKey);

    if (!messages || messages.length === 0) {
      return [];
    }

    // Return a mock summary
    return [
      {
        conversationId: `mock-${conversationKey}`,
        startupId,
        startupName: 'Demo Startup',
        investorId,
        summary: 'Previous conversation about startup analysis',
        topics: ['market', 'team', 'traction'],
        messageCount: messages.length,
        totalTokens: messages.reduce((acc, m) => acc + Math.floor(m.content.length / 4), 0),
        createdAt: new Date().toISOString(),
        lastMessageAt: new Date().toISOString(),
      },
    ];
  }

  /**
   * Clear conversation history.
   */
  async clearHistory(investorId: string, startupId: string, _permanent?: boolean): Promise<void> {
    const conversationKey = `${investorId}-${startupId}`;
    this.conversationMessages.delete(conversationKey);
  }

  /**
   * Generate mock response based on query content.
   * Includes numbered citation markers [1], [2] that correspond to
   * sources returned by getMockSources() so the inline citation UI works.
   */
  private generateMockResponse(query: string, context?: DDAssistantContext['startupContext']): string {
    const lowerQuery = query.toLowerCase();
    const startupName = context?.name || 'this startup';

    if (lowerQuery.includes('market') || lowerQuery.includes('tam')) {
      return `Based on the available data, ${startupName} operates in a rapidly growing market estimated at $45B globally [1]. The serviceable addressable market (SAM) for their specific niche is approximately $8B, with strong growth projections of 15-20% CAGR over the next 5 years [2].

**Key Market Insights:**
- Growing demand driven by digital transformation [1]
- Underserved SMB segment presents opportunity [2]
- Regulatory tailwinds in target markets
`;
    }

    if (lowerQuery.includes('team') || lowerQuery.includes('founder')) {
      return `The founding team brings relevant experience to this venture [1]:

**Team Highlights:**
- Previous successful exit (acqui-hired by Salesforce in 2021) [1]
- Deep domain expertise in AI/ML [2]
- Complementary skill sets across technical and business functions
- Strong advisory board including industry veterans [2]
`;
    }

    if (lowerQuery.includes('traction') || lowerQuery.includes('revenue') || lowerQuery.includes('growth')) {
      return `${startupName} shows promising early traction metrics [1]:

**Traction Summary:**
- MRR: $18,000 (growing 15% MoM) [1]
- Active Users: 1,000+ [2]
- Customer Retention: Strong (specific NRR data pending)
- Growth trajectory suggests product-market fit [1]
`;
    }

    if (lowerQuery.includes('risk') || lowerQuery.includes('concern')) {
      return `Based on the analysis, key risks and considerations include [1]:

**Risk Assessment:**
1. **Market Competition** - Competing with well-funded incumbents (Zapier, Make) [1]
2. **Execution Risk** - Scaling team while maintaining culture [2]
3. **Technical Risk** - AI model accuracy at scale [1]
4. **Capital Efficiency** - Burn rate optimization needed [2]

**Mitigating Factors:**
- Experienced team with prior exits [2]
- Differentiated technology approach
- Strong early customer feedback
`;
    }

    // Default response
    return `Thank you for your question about ${startupName}. Based on the available information [1]:

This startup demonstrates characteristics typical of early-stage ventures in the Enterprise SaaS space. Their approach to AI-powered workflow automation addresses a real pain point for SMBs [1].

**Key Observations:**
- Clear problem-solution fit [1]
- Growing market opportunity [2]
- Experienced founding team [2]
- Early but promising traction

Would you like me to dive deeper into any specific aspect of the analysis?
`;
  }

  /**
   * Get mock sources based on query type.
   * Always returns at least 2 sources so citation markers [1], [2] in
   * generateMockResponse() have matching entries.
   */
  private getMockSources(query: string): DDSource[] {
    const lowerQuery = query.toLowerCase();

    if (lowerQuery.includes('pitch') || lowerQuery.includes('deck')) {
      return [
        {
          type: 'document',
          label: 'Pitch Deck Q4 2024.pdf',
          snippet: 'TechFlow AI provides enterprise-grade workflow automation...',
          relevance: 0.92,
        },
        {
          type: 'document',
          label: 'Company Overview.pdf',
          snippet: 'Founded in 2023, targeting the $45B workflow automation market...',
          relevance: 0.84,
        },
      ];
    }

    if (lowerQuery.includes('financial') || lowerQuery.includes('revenue') || lowerQuery.includes('traction') || lowerQuery.includes('growth')) {
      return [
        {
          type: 'document',
          label: 'Financial Projections 2025.xlsx',
          snippet: 'Projected ARR growth of 200% year-over-year...',
          relevance: 0.89,
        },
        {
          type: 'document',
          label: 'Pitch Deck Q4 2024.pdf',
          snippet: 'Current MRR: $18,000 with 15% month-over-month growth...',
          relevance: 0.85,
        },
      ];
    }

    if (lowerQuery.includes('team') || lowerQuery.includes('founder')) {
      return [
        {
          type: 'document',
          label: 'Team Bios.pdf',
          snippet: 'Alex Chen, CEO - Previously founded DataSync (acquired by Salesforce)...',
          relevance: 0.94,
        },
        {
          type: 'document',
          label: 'Pitch Deck Q4 2024.pdf',
          snippet: 'Leadership team with combined 30+ years in enterprise SaaS...',
          relevance: 0.82,
        },
      ];
    }

    if (lowerQuery.includes('market') || lowerQuery.includes('tam')) {
      return [
        {
          type: 'document',
          label: 'Market Analysis 2025.pdf',
          snippet: 'Global workflow automation market estimated at $45B with 15-20% CAGR...',
          relevance: 0.91,
        },
        {
          type: 'document',
          label: 'Pitch Deck Q4 2024.pdf',
          snippet: 'Serviceable addressable market of $8B in SMB segment...',
          relevance: 0.87,
        },
      ];
    }

    if (lowerQuery.includes('risk') || lowerQuery.includes('concern')) {
      return [
        {
          type: 'document',
          label: 'Pitch Deck Q4 2024.pdf',
          snippet: 'Competitive landscape includes Zapier, Make, and emerging AI players...',
          relevance: 0.88,
        },
        {
          type: 'document',
          label: 'Team Bios.pdf',
          snippet: 'Experienced founding team with prior exits mitigates execution risk...',
          relevance: 0.83,
        },
      ];
    }

    // Default: general sources
    return [
      {
        type: 'document',
        label: 'Pitch Deck Q4 2024.pdf',
        snippet: 'TechFlow AI provides enterprise-grade workflow automation...',
        relevance: 0.90,
      },
      {
        type: 'document',
        label: 'Company Overview.pdf',
        snippet: 'Early-stage Enterprise SaaS startup targeting SMB workflow automation...',
        relevance: 0.85,
      },
    ];
  }

  /**
   * Get suggested follow-up questions
   */
  private getSuggestedQuestions(lastQuery: string): string[] {
    const lowerQuery = lastQuery.toLowerCase();

    if (lowerQuery.includes('market')) {
      return [
        'How does their competitive positioning compare?',
        'What are the barriers to entry in this market?',
        'Who are their main competitors?',
      ];
    }

    if (lowerQuery.includes('team')) {
      return [
        "What is the team's track record with previous ventures?",
        'Are there any key hires planned?',
        'What does the advisory board look like?',
      ];
    }

    return [
      'What are the key risks for this startup?',
      'How does their traction compare to similar companies?',
      'What is their competitive advantage?',
    ];
  }

  /**
   * Extract topics from query
   */
  private extractTopics(query: string): string[] {
    const topics: string[] = [];
    const lowerQuery = query.toLowerCase();

    if (lowerQuery.includes('market') || lowerQuery.includes('tam')) topics.push('market');
    if (lowerQuery.includes('team') || lowerQuery.includes('founder')) topics.push('team');
    if (lowerQuery.includes('traction') || lowerQuery.includes('growth')) topics.push('traction');
    if (lowerQuery.includes('risk')) topics.push('risks');
    if (lowerQuery.includes('financ') || lowerQuery.includes('revenue')) topics.push('financials');
    if (lowerQuery.includes('compet')) topics.push('competition');
    if (lowerQuery.includes('product')) topics.push('product');

    return topics.length > 0 ? topics : ['general'];
  }

  /**
   * Simulate network delay
   */
  private async simulateDelay(ms: number): Promise<void> {
    return new Promise((resolve) => setTimeout(resolve, ms));
  }
}
